import React, { useMemo, useState } from "react";
import "./EAProductBanner.scss";
import EASkeletonLoader from "../EASkeletonLoader";
import { useTranslation } from "react-i18next";
import { MdImageNotSupported } from "react-icons/md";
import EAButton from "../Buttons/EAButton";
import EAModal from "../Modal/EAModal";
import EADivider from "../EADivider";
import { FaStar } from "react-icons/fa";
import useAuth from "../../Login/hooks/useAuth";
import { LOGIN_PAGE_WITH_RETURN } from "../../../routes";
import { useNavigate } from "react-router";
import { stripHtml } from "../../../../utils/stripHtml";

interface IProps {
  title: string;
  description: string;
  pictureUrl: string;
  buttonTitle: string;
  buttonStartIcon?: React.ReactNode;
  buttonVariant?: "filled" | "outlined" | "text";
  mainPrice: string | number;
  additionalPriceInfo?: string;
  onClick?: () => void;
  loading?: boolean;
  expanded?: boolean;
  children?: React.ReactNode;
  highlightBanner?: boolean;
  bannerLabel?: string;
  crossedPrice?: string;
  highlightedFeatures?: string[];
  loginRequired?: boolean;
  hidePrice?: boolean;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  buttonCaption?: string;
}

const EAProductBanner: React.FC<IProps> = ({
  title,
  description,
  pictureUrl,
  loading = false,
  buttonTitle,
  buttonStartIcon = <></>,
  buttonVariant = "filled",
  mainPrice,
  additionalPriceInfo,
  onClick = () => {},
  expanded = false,
  children,
  highlightBanner = false,
  bannerLabel,
  crossedPrice,
  highlightedFeatures = [],
  loginRequired = false,
  hidePrice = false,
  buttonDisabled = false,
  buttonLoading = false,
  buttonCaption,
}) => {
  const { t } = useTranslation();
  const [readMore, setReadMore] = useState(false);
  const [loginRequiredModalOpen, setLoginRequiredModalOpen] = useState(false);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const descriptionPreview = useMemo(() => stripHtml(description), [description]);

  const handleClick = () => {
    if (loginRequired && !isLoggedIn) {
      setLoginRequiredModalOpen(true);
    } else {
      onClick();
    }
  };

  if (loading) {
    return <EASkeletonLoader height="300px" />;
  }

  return (
    <>
      <div className={`product-banner-container ${highlightBanner ? "highlight" : ""}`}>
        {bannerLabel && <div className="product-banner__label">{bannerLabel}</div>}
        <div className="product-banner-primary-wrapper">
          {pictureUrl ? (
            <img className="product-banner__image" alt="Miniatura zdjęcia produktu" src={pictureUrl} />
          ) : (
            <div className="product-banner__empty-image">
              <MdImageNotSupported />
            </div>
          )}

          <div className="product-banner__right-col">
            <div className="product-banner__content">
              <h4>{title}</h4>
              {highlightedFeatures.length > 0 && (
                <div className="product-banner__content-highlighted-features">
                  {highlightedFeatures.map((item, index) => (
                    <p key={index} className="body-text-small text-bold product-banner__content-highlighted-feature">
                      <FaStar style={{ paddingTop: "2px" }} /> {t(item)}
                    </p>
                  ))}
                </div>
              )}
              <p className="product-banner__content-description">{descriptionPreview}</p>
              <EAButton variant="text" title={t("Czytaj więcej")} noMargins onClick={() => setReadMore(true)} />
            </div>

            <div className={`product-banner__summary ${hidePrice ? "product-banner__summary--no-price" : ""}`}>
              {!hidePrice && (
                <div className="product-banner__price">
                  <h5>
                    {mainPrice} <span className="product-banner__crossed-price">{crossedPrice}</span>
                  </h5>
                  <span className="body-small">{additionalPriceInfo && t(additionalPriceInfo)}</span>
                </div>
              )}

              <div className="product-banner__button">
                <EAButton
                  title={buttonTitle}
                  startIcon={buttonStartIcon}
                  onClick={handleClick}
                  fullWidth
                  variant={buttonVariant}
                  disabled={buttonDisabled}
                  loading={buttonLoading}
                />
                {buttonCaption && (
                  <p className="caption text-color-error text-centered product-banner__button-caption">
                    {buttonCaption}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {expanded && children && (
          <div className="product-banner__expanded-content">
            <EADivider />
            {children}
          </div>
        )}
      </div>

      <EAModal
        isOpen={readMore}
        title={title}
        onClose={() => setReadMore(false)}
        cancelButtonLabel={t("przeczytalem")}
        hideAcceptButton
      >
        <p dangerouslySetInnerHTML={{ __html: description }}></p>
      </EAModal>

      <EAModal
        isOpen={loginRequiredModalOpen}
        title={t("ta_opcja_dostepna_tylko_po_zalogowaniu")}
        onClose={() => setLoginRequiredModalOpen(false)}
        acceptButtonLabel={t("przejdz_do_logowania_rejestracji")}
        cancelButtonLabel={t("zamknij")}
        onAccept={() => navigate(LOGIN_PAGE_WITH_RETURN)}
      >
        <p>{t("dedykowane_dla_stalych_klientow_info")}</p>
      </EAModal>
    </>
  );
};

export default EAProductBanner;
